import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { Fragment, useEffect, useState } from 'react';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import styles from './destination-editor.module.scss';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import routes from '../../../modules/routes/index.routes';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { DestinationForm } from '../../../modules/components/forms/destination/destination.form';
import { DestinationCreatives } from '../../../modules/components/forms/destination/destination-creatives/destination-creatives';
import { DestinationModel } from '../../../modules/models/destination.model';
import { useList } from '../../../modules/hooks/tools/use-list';
import { CreativeModel } from '../../../modules/models/creative.model';
import { DestinationsApi } from '../../../modules/apis/destinations.api';

export const DestinationEditor = observer(() => {
	const { destinationStore, creativeStore } = useStores<IAdminxStore>();
	const [isFormClicked, setFormClicked] = useState(false);
	const [isLoadingCreatives, setIsLoadingCreatives] = useState(false);
	const { destinationId } = useParams();

	const { dispatchLog } = useLogs();
	const navigate = useNavigateParams();

	const crudStore = destinationStore.getCrud();
	const data = crudStore.getData();
	const formStore = crudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isLoading = crudStore.getIsLoading();
	const isSuccess = crudStore.getIsSuccess();
	const error = crudStore.getHttpError();

	const isLoadingEdit = Boolean(destinationId && (!data?.getId() || isLoadingCreatives));

	useList<CreativeModel>(creativeStore.getListStore());

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		crudStore.reset();

		if (destinationId && crudStore.data.id !== destinationId) {
			setIsLoadingCreatives(true);
			crudStore
				.get(destinationId)
				.then(async response => {
					if (!response?.id) {
						return;
					}

					const destination = new DestinationModel(response);

					if (!destination.getCreatives().length) {
						try {
							const creativeLinks = await DestinationsApi.listCreatives(destinationId);
							destination.setCreatives(creativeLinks);
						} catch {
							dispatchLog({
								msg: 'Failed to load destination creatives',
								type: DispatchLogsTypes.Error,
							});
						}
					}

					crudStore.setData(destination);
				})
				.finally(() => {
					setIsLoadingCreatives(false);
				});
		}

		if (isSuccess) {
			navigate(routes.destinations.index());

			dispatchLog({
				msg: `Destination was successfully ${destinationId ? 'updated' : 'created'}`,
				type: DispatchLogsTypes.Success,
			});

			crudStore.setIsSuccess(false);
		}
	}, [isSuccess, destinationId]);

	const handleSave = () => {
		setFormClicked(true);

		if (destinationId) {
			crudStore.edit();
		} else {
			crudStore.create();
		}
	};

	return (
		<Fragment>
			<Disable is={isLoading || isLoadingEdit}>
				<Page>
					<Pagebar>
						<PagebarTitle unstyled={true}>
							<div className={styles.title}>{destinationId ? 'Edit' : 'Create'} Destination</div>
						</PagebarTitle>
					</Pagebar>
					<div className={styles.formColumns}>
						<div className={styles.formColumn}>
							<DestinationForm isEdit={Boolean(destinationId)} />
						</div>
						{destinationId ? (
							<div className={styles.creativesColumn}>
								<DestinationCreatives isLoadingCreatives={isLoadingCreatives} />
							</div>
						) : (
							<div className={styles.creativesColumn}>
								<div className={styles.creativesHint}>Save the destination first to attach creatives.</div>
							</div>
						)}
					</div>
				</Page>
			</Disable>
			<BottomBar>
				{error ? <ErrorMsg error={error} /> : null}
				<BottomBarActions>
					<PrimaryButton
						onClick={handleSave}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'save_destination_button')] }}
						isRotateIcon={isLoading}
						icon={isLoading || isLoadingEdit ? 'loading-01' : destinationId ? 'save-01' : 'send-03'}>
						{destinationId ? 'Save' : 'Create'}
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</Fragment>
	);
});
